import * as React from "react";
import IconTextButton from "../iconTextButton/IconTextButton";
import { EntityType } from "../../types/EntityType";
import IRepertoireComponentDataItem from "../../../redux/types/IRepertoireComponentDataItem";
import { IValidationMessage } from "../../types/IValidationMessage";
import { useAddToPackageContext } from "./AddToPackageContext";
import { IAddtoPackageResult } from "../../types/IAddtoPackageResult";
import { CURRENT_USER_EMAIL_KEY, CURRENT_USER_NAME_KEY } from "../../../redux/services/AccountSessionStorage";
import { useCallback, useState } from "react";

export interface IAddToPackageButtonProps {
    entityType: EntityType;
    entityId: number;
    addToPackages: (userName: string, entityType: string, entityIds: number[]) => Promise<IAddtoPackageResult> | IAddtoPackageResult;
    removeFromPackages?: (userName: string, entityType: string, entityIds: number[]) => Promise<IAddtoPackageResult> | IAddtoPackageResult;
    resultPageData: IRepertoireComponentDataItem;
    wrapperClassName: string;
    disabled?: boolean;
}

const AddToPackageButton: React.FC<IAddToPackageButtonProps> = ({
    entityType,
    entityId,
    addToPackages,
    removeFromPackages,
    resultPageData,
    wrapperClassName,
    disabled
}) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [messageText, setMessageText] = useState("");
    const [messageType, setMessageType] = useState<"success" | "error" | undefined>();

    const ctx = useAddToPackageContext();
    const addedIds = ctx?.addedIds ?? [];
    const addIds = ctx?.addIds ?? (() => undefined);
    const removeIds = ctx?.removeIds ?? (() => undefined);

    const getText = (key: string, fallback: string): string =>
        resultPageData?.fields?.find(
            field => field.name?.toString() === key
        )?.data || fallback;

    const isAdded = addedIds.includes(entityId);

    const handleClick = useCallback(async () => {
        if (disabled || isProcessing || !entityId || !addToPackages || (isAdded && !removeFromPackages)) {
            return;
        }

        const userName = sessionStorage.getItem(CURRENT_USER_EMAIL_KEY) || sessionStorage.getItem(CURRENT_USER_NAME_KEY);
        if (!userName) return;

        setIsProcessing(true);

        try {
            const operation = isAdded ? removeFromPackages(userName, entityType, [entityId]) : addToPackages(userName, entityType, [entityId]);

            const response = await Promise.resolve(operation);

            const messageKey = response?.messages?.[0]?.messageKey;

            const fallbackMessage = response?.success
                ? isAdded
                    ? "Product removed from package"
                    : "Product added to package"
                : "Could not update package";

            const translatedMessage = messageKey
                ? getText(messageKey, fallbackMessage)
                : fallbackMessage;

            if (response?.success) {
                if (isAdded) {
                    removeIds([entityId]);
                } else {
                    addIds([entityId]);
                }

                setMessageType("success");
            } else {
                setMessageType("error");
            }

            setMessageText(translatedMessage);

            window.setTimeout(() => setMessageText(""), 2000);
        } catch {
            setMessageType("error");
            setMessageText("");
        } finally {
            setIsProcessing(false);
        }
    }, [disabled, isProcessing, entityId, entityType, isAdded, addToPackages, removeFromPackages, resultPageData, addIds, removeIds]);

    const messageClass = messageType === "error" ? "addtopackageerrorMessage" : "addtopackagesuccessMessage";

    const actionTooltip = isProcessing
        ? isAdded
            ? getText(
                "removingFromPackageLabel",
                "Removing..."
            )
            : getText(
                "addingToPackageLabel",
                "Adding..."
            )
        : isAdded
            ? getText(
                "removeFromPackageLabel",
                "Remove from Package"
            )
            : getText(
                "addToPackageLabel",
                "Add to Package"
            );

    return (
        <div className="tableCell specialColumn">
            {messageText && (
                <div className={`messageBox ${messageClass}`}>
                    {messageText}
                </div>
            )}

            <div className={wrapperClassName}>
                <IconTextButton
                    icon={isAdded ? "icon ms-Icon ms-Icon--CheckMark" : "icon ms-Icon ms-Icon--AddIn"}
                    onClick={handleClick}
                    disabled={disabled || isProcessing || (isAdded && !removeFromPackages)}
                    tooltip={actionTooltip}
                />
            </div>
        </div>
    );
};

export default AddToPackageButton;
